import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { MdCloseFullscreen, MdEast, MdWest } from "react-icons/md";

interface FullscreenCodeProps {
  currentStepImage: string;
  fullCodeImage: string;
  setShowCodeFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  initialCodeTab?: number;
}

// Constants definition
const BUTTON_PADDING = 24; //px

const FullscreenCode: React.FC<FullscreenCodeProps> = ({
  currentStepImage,
  fullCodeImage,
  setShowCodeFullscreen,
  initialCodeTab,
}) => {
  // States definition
  const [selectedTab, setSelectedTab] = useState(initialCodeTab || 0);
  const [buttonPosition, setButtonPosition] = useState({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });

  // Image panel reference
  const imgPanelRef: React.MutableRefObject<HTMLDivElement | null> =
    useRef(null);

  /**
   * Function use to control the behaviour of the floating arrow button
   */
  const handleArrowButtonClick = () => {
    // Set the new state
    setSelectedTab(selectedTab === 0 ? 1 : 0);
  };

  /**
   * Function used to update the limits of the panel based on the
   * window size
   */
  useEffect(() => {
    // Dynamically calculate the viewport size
    const updatePosition = () => {
      // Get the image position and size parameters
      if (imgPanelRef.current) {
        const { x, y, width, height } =
          imgPanelRef.current.getBoundingClientRect();

        // Define the position based on the selected tab
        if (selectedTab === 0) {
          setButtonPosition({
            x: x + width + BUTTON_PADDING,
            y: y + height / 2 - 48,
          });
        } else {
          setButtonPosition({
            x: x - 48 - BUTTON_PADDING,
            y: y + height / 2 - 48,
          });
        }
      }
    };

    // Call updatePosition initially
    updatePosition();

    // Observe size changes using ResizeObserver
    const resizeObserver = new ResizeObserver(() => {
      updatePosition();
    });

    // Start observing the target element
    if (imgPanelRef.current) {
      resizeObserver.observe(imgPanelRef.current);
    }

    // Cleanup observer on unmount
    return () => {
      if (imgPanelRef.current) {
        resizeObserver.unobserve(imgPanelRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [selectedTab, imgPanelRef]);

  return (
    <motion.div
      className="fixed inset-0 w-full h-full p-5 bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] inline-flex items-center justify-center z-[110]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: 0.1, ease: "easeOut" },
      }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <div
        className="absolute w-full h-full z-20"
        onClick={() => setShowCodeFullscreen(false)}
      />
      <div className="absolute max-w-[90vw] max-h-[90vh] z-30 inline-flex flex-col gap-6">
        <div className="w-fit h-[52px] px-2.5 py-2 rounded-[40px] backdrop-blur-[20px] justify-start items-center inline-flex gap-2">
          {/* Show only the available options */}
          {currentStepImage && (
            <div
              className={`px-3 py-1 rounded-[32px] backdrop-blur-[20px] justify-center items-center inline-flex cursor-pointer ${
                selectedTab === 0
                  ? "bg-neutral-700/60"
                  : "hover:bg-neutral-400/60 ease-in-out duration-150"
              }`}
              onClick={() => setSelectedTab(0)}
            >
              <div className="text-white text-[23px] font-semibold font-poppins leading-7 whitespace-nowrap">
                Current Step
              </div>
            </div>
          )}
          {fullCodeImage && (
            <div
              className={`px-3 py-1 rounded-[32px] backdrop-blur-[20px] justify-center items-center inline-flex cursor-pointer ${
                selectedTab === 1
                  ? "bg-neutral-700/60"
                  : "hover:bg-neutral-400/60 ease-in-out duration-150"
              }`}
              // If current step exists, then this assign to the second tab. Otherwise to the first
              onClick={() => setSelectedTab(currentStepImage ? 1 : 0)}
            >
              <div className="text-white text-[23px] font-semibold font-poppins leading-7 whitespace-nowrap">
                View Full Code so Far
              </div>
            </div>
          )}
        </div>
        <motion.div
          ref={imgPanelRef}
          className="w-fit max-w-full max-h-full p-6 bg-white rounded-2xl flex-col justify-center items-center gap-12 inline-flex z-30 select-none relative overflow-hidden"
        >
          <img
            className="w-auto h-auto"
            src={
              // If both of them are available, then apply the selection tab logic
              currentStepImage && fullCodeImage
                ? selectedTab === 0
                  ? currentStepImage
                  : fullCodeImage
                : // Otherwise just show the image available
                  currentStepImage || fullCodeImage
            }
          />
          <div
            className="absolute w-10 h-10 top-0 right-0 bg-neutral-700/25 origin-top-right hover:scale-[1.1] ease-in-out duration-150 rounded-bl-lg backdrop-blur-sm justify-center items-center inline-flex cursor-pointer"
            onClick={() => setShowCodeFullscreen(false)}
          >
            <MdCloseFullscreen className="w-8 h-8 fill-white" />
          </div>
        </motion.div>
      </div>
      {/* Activate the button just if both images are available */}
      {currentStepImage && fullCodeImage && (
        <motion.div
          className="absolute inset-0 z-40"
          initial={{ x: window.innerWidth / 2, y: window.innerHeight / 2 }}
          animate={{ ...buttonPosition }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <div
            className="w-12 h-12 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-center items-center inline-flex cursor-pointer"
            onClick={handleArrowButtonClick}
          >
            <div className="w-6 h-6 relative">
              {selectedTab === 0 ? (
                <MdEast className="w-full h-full fill-secondary-600" />
              ) : (
                <MdWest className="w-full h-full fill-secondary-600" />
              )}
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default FullscreenCode;
